import React, { useEffect, useCallback, useContext, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export interface NavigationGuardProps {
  /**
   * Whether navigation should be blocked
   */
  shouldBlock: boolean;
  
  /**
   * Message to show in the confirmation dialog
   */
  message?: string;
  
  /**
   * Title for the confirmation dialog
   */
  title?: string;
  
  /**
   * Callback when user confirms navigation
   */
  onConfirm?: () => void;
  
  /**
   * Callback when user cancels navigation
   */
  onCancel?: () => void;
}

/**
 * Hook for blocking browser navigation (refresh, close, back button)
 */
export function useBeforeUnload(shouldBlock: boolean, message?: string) {
  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Modern browsers ignore the custom message and show their own
      event.returnValue = message || 'You have unsaved changes. Are you sure you want to leave?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, message]);
}

/**
 * NavigationGuard component that blocks navigation when conditions are met
 */
export const NavigationGuard: React.FC<NavigationGuardProps> = ({
  shouldBlock,
  message = 'You have unsaved changes. Are you sure you want to leave?',
  title = 'Confirm Navigation',
  onConfirm,
  onCancel,
}) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState<(() => void) | null>(null);

  // Block React Router navigation (BrowserRouter compatible)
  const blocker = useBrowserRouterBlocker(shouldBlock);

  // Block browser navigation (refresh, close, back button)
  useBeforeUnload(shouldBlock, message);

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowDialog(true);
      setPendingNavigation(() => blocker.proceed);
    }
  }, [blocker.proceed, blocker.state]);

  const handleConfirm = () => {
    setShowDialog(false);
    onConfirm?.();
    
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    onCancel?.();
    
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    setPendingNavigation(null);
  };

  return (
    <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            Stay on Page
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            Leave Page
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Hook that combines NavigationGuard component with useBeforeUnload
 */
export function useNavigationGuard(
  shouldBlock: boolean,
  options: {
    message?: string;
    title?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  } = {}
) {
  const NavigationGuardComponent = useCallback(
    () => (
      <NavigationGuard
        shouldBlock={shouldBlock}
        message={options.message}
        title={options.title}
        onConfirm={options.onConfirm}
        onCancel={options.onCancel}
      />
    ),
    [shouldBlock, options.message, options.title, options.onConfirm, options.onCancel]
  );

  return NavigationGuardComponent;
}

type BlockerState = 'unblocked' | 'blocked' | 'proceeding';

interface BrowserRouterBlocker {
  state: BlockerState;
  proceed: () => void;
  reset: () => void;
}

/**
 * Replacement for react-router's useBlocker that works with BrowserRouter.
 * It relies on the unstable navigation context exposed by react-router.
 */
function useBrowserRouterBlocker(shouldBlock: boolean): BrowserRouterBlocker {
  const navigationContext = useContext(
    UNSAFE_NavigationContext
  ) as { navigator?: { block?: (cb: (tx: any) => void) => () => void } } | null;
  const [state, setState] = useState<BlockerState>('unblocked');
  const transitionRef = useRef<{ retry: () => void } | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!shouldBlock) {
      transitionRef.current = null;
      setState('unblocked');
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return;
    }

    const navigator = navigationContext?.navigator;
    const blockFn = navigator?.block;

    if (typeof blockFn !== 'function') {
      transitionRef.current = null;
      setState('unblocked');
      const env = (import.meta as any)?.env;
      if (!env || !env.PROD) {
        console.warn('NavigationGuard: current router does not support navigation blocking.');
      }
      return;
    }

    const unblock = blockFn((tx: any) => {
      transitionRef.current = {
        retry() {
          unblock();
          tx.retry();
        },
      };
      setState('blocked');
    });

    unblockRef.current = () => {
      unblock();
      unblockRef.current = null;
    };

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      } else {
        unblock();
      }
      transitionRef.current = null;
      setState('unblocked');
    };
  }, [navigationContext, shouldBlock]);

  const proceed = useCallback(() => {
    if (transitionRef.current) {
      setState('proceeding');
      transitionRef.current.retry();
      transitionRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    transitionRef.current = null;
    setState('unblocked');
  }, []);

  return { state, proceed, reset };
}
